<script setup lang="ts">
import type { UnansweredQuestion } from "@maimu/contracts";
import { computed, onMounted, ref } from "vue";

import { api } from "@/lib/api.js";

/**
 * The learning loop's exit side (blueprint §6, Phase-1 reference §11 sidebar).
 *
 * Phase 1 showed this queue to everyone; here it is an admin view, but the copy is
 * kept — staff learned to read it, and the empty state saying "if the assistant gets
 * stuck, the question appears here" is what makes a low-confidence answer feel like
 * the system working rather than failing.
 */
const questions = ref<UnansweredQuestion[]>([]);
const drafts = ref<Record<string, string>>({});
const busy = ref<string | null>(null);
const error = ref<string | null>(null);
const loading = ref(true);
const showAnswered = ref(false);

const visible = computed(() => questions.value.filter((q) => (showAnswered.value ? true : q.status === "open")));
const openCount = computed(() => questions.value.filter((q) => q.status === "open").length);

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    questions.value = await api.unanswered("all");
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "Küsimusi ei õnnestunud laadida.";
  } finally {
    loading.value = false;
  }
}

async function submit(question: UnansweredQuestion): Promise<void> {
  const answer = (drafts.value[question.id] ?? "").trim();
  if (!answer) return;

  busy.value = question.id;
  error.value = null;
  try {
    const updated = await api.answerUnanswered(question.id, answer);
    questions.value = questions.value.map((row) => (row.id === updated.id ? updated : row));
    delete drafts.value[question.id];
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "Vastuse salvestamine ebaõnnestus.";
  } finally {
    busy.value = null;
  }
}

async function dismiss(question: UnansweredQuestion): Promise<void> {
  busy.value = question.id;
  try {
    await api.dismissUnanswered(question.id);
    questions.value = questions.value.map((row) =>
      row.id === question.id ? { ...row, status: "dismissed" as const } : row,
    );
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "Küsimuse eemaldamine ebaõnnestus.";
  } finally {
    busy.value = null;
  }
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

onMounted(load);
</script>

<template>
  <section class="queue">
    <header class="queue-head">
      <div>
        <h2>Vastamata küsimused</h2>
        <p class="hint">
          Vastus salvestub siia. Teadmusesse jõuab see käsuga
          <code>pnpm kb:export-learned</code> ja seejärel <code>pnpm kb:ingest</code> — nii jääb inimene vahele.
        </p>
      </div>
      <label class="toggle">
        <input v-model="showAnswered" type="checkbox" />
        Näita ka vastatuid
      </label>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="hint">Laen…</p>

    <p v-else-if="!openCount && !showAnswered" class="empty">
      Praegu ei ole vastamata küsimusi. Kui assistent jääb hätta, ilmub küsimus siia.
    </p>

    <ul v-else class="list">
      <li v-for="question in visible" :key="question.id" :class="question.status">
        <div class="meta">
          <span class="badge" :class="question.status">
            {{ question.status === "open" ? "ootab" : question.status === "answered" ? "vastatud" : "eemaldatud" }}
          </span>
          <span>{{ when(question.createdAt) }}</span>
          <span v-if="question.askedBy">· {{ question.askedBy }}</span>
        </div>

        <p class="question">{{ question.question }}</p>
        <p class="reason">{{ question.reason }}</p>

        <template v-if="question.status === 'open'">
          <textarea
            v-model="drafts[question.id]"
            rows="4"
            placeholder="Kirjuta siia õige vastus..."
            :disabled="busy === question.id"
          />
          <div class="actions">
            <button
              class="btn"
              type="button"
              :disabled="busy === question.id || !(drafts[question.id] ?? '').trim()"
              @click="submit(question)"
            >
              Lisa vastus teadmusesse
            </button>
            <button class="btn-ghost" type="button" :disabled="busy === question.id" @click="dismiss(question)">
              Eemalda järjekorrast
            </button>
          </div>
        </template>

        <blockquote v-else-if="question.answer" class="answer">
          {{ question.answer }}
          <footer v-if="question.answeredBy">— {{ question.answeredBy }}</footer>
        </blockquote>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.queue {
  max-width: 860px;
  margin: 0 auto;
  padding: 1.4rem 1rem 3rem;
}

.queue-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

h2 {
  margin: 0 0 0.3rem;
  font-size: 1.05rem;
}

.hint {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0 0 0.9rem;
}

code {
  background: var(--soft);
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
}

.toggle {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: var(--muted);
}

.empty,
.error {
  border: 1px dashed var(--line);
  border-radius: 10px;
  padding: 1rem;
  color: var(--muted);
  font-size: 0.88rem;
}

.error {
  border-style: solid;
  border-color: var(--warn);
  color: var(--warn);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

li {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  background: var(--paper);
}

li.dismissed {
  opacity: 0.55;
}

.meta {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.75rem;
  color: var(--muted);
  margin-bottom: 0.45rem;
}

.badge {
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  font-weight: 600;
  border: 1px solid var(--line);
}

.badge.open {
  color: var(--warn);
  border-color: var(--warn);
}

.badge.answered {
  color: var(--green);
  border-color: var(--green);
}

.question {
  margin: 0 0 0.25rem;
  font-weight: 600;
  color: var(--ink);
}

.reason {
  margin: 0 0 0.6rem;
  font-size: 0.8rem;
  color: var(--muted);
}

textarea {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  font: inherit;
  font-size: 0.88rem;
  resize: vertical;
  background: #fff;
  color: var(--ink);
}

.actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.answer {
  margin: 0;
  padding: 0.6rem 0.8rem;
  border-left: 3px solid var(--green);
  background: var(--soft);
  border-radius: 0 8px 8px 0;
  font-size: 0.88rem;
  white-space: pre-wrap;
}

.answer footer {
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: var(--muted);
}
</style>
